import type Phaser from 'phaser';
import { SLICE, TEX } from './assets';
import { rasterUrl } from './raster';

const BG_PATH = 'assets/bg/cc_bg_gameplay_thistlewood@1x.jpg';
const WOOD_PATH = 'assets/ui/cc_ui_panel_wood@1x.png';

export const LOADER_TEX = {
  far: 'glen-load-far',
  mid: 'glen-load-mid',
  near: 'glen-load-near',
  light: 'glen-load-light',
  honey: 'glen-load-honey',
  shimmer: 'glen-load-shimmer',
  fly: 'glen-load-fly',
  leaf: 'glen-load-leaf',
  seed: 'glen-load-seed',
  acorn: 'glen-load-acorn',
} as const;

let bgImage: HTMLImageElement | null = null;
let woodImage: HTMLImageElement | null = null;
let assetsTask: Promise<void> | null = null;

function decode(src: string): Promise<HTMLImageElement> {
  const image = new Image();
  const done = new Promise<HTMLImageElement>((resolve, reject) => {
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(src));
  });
  image.src = src;
  if (image.decode) return image.decode().then(() => image);
  return done;
}

async function loadPacked(path: string): Promise<HTMLImageElement | null> {
  const packed = rasterUrl(path);
  try {
    return await decode(packed);
  } catch {
    if (packed === path) return null;
    try {
      return await decode(path);
    } catch {
      return null;
    }
  }
}

/** Glen painting and carved plank. Started on the title so the gate does not wait on the menu queue. */
export function preloadLoaderAssets(): Promise<void> {
  if (assetsTask) return assetsTask;
  if (typeof Image === 'undefined') {
    assetsTask = Promise.resolve();
    return assetsTask;
  }
  assetsTask = Promise.all([loadPacked(BG_PATH), loadPacked(WOOD_PATH)]).then(([bg, wood]) => {
    bgImage = bg;
    woodImage = wood;
  });
  return assetsTask;
}

export async function installLoaderAssets(_scene: Phaser.Scene): Promise<void> {
  // Decode only. Registering bg-game or panel-wood here races the duel queue,
  // which then throws "texture already in use" and never finishes.
  await preloadLoaderAssets();
}

export function loaderWoodImage(): (HTMLImageElement & { width: number; height: number }) | null {
  if (!woodImage || woodImage.naturalWidth < 8) return null;
  return woodImage;
}

export function loaderGlenImage(): (HTMLImageElement & { width: number; height: number }) | null {
  if (!bgImage || bgImage.naturalWidth < 8) return null;
  return bgImage;
}

interface Measured {
  width: number;
  height: number;
}

function realSource(scene: Phaser.Scene, key: string): (CanvasImageSource & Measured) | null {
  if (!scene.textures.exists(key)) return null;
  const source = scene.textures.get(key).getSourceImage() as CanvasImageSource & { width?: number; height?: number };
  const width = source.width ?? 0;
  const height = source.height ?? 0;
  if (width < 8 || height < 8) return null;
  return source as CanvasImageSource & Measured;
}

function makeCanvas(
  scene: Phaser.Scene,
  key: string,
  width: number,
  height: number,
): CanvasRenderingContext2D | null {
  if (scene.textures.exists(key)) scene.textures.remove(key);
  const texture = scene.textures.createCanvas(key, width, height);
  if (!texture) return null;
  const ctx = texture.getContext();
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  return ctx;
}

function publish(scene: Phaser.Scene, key: string): void {
  const texture = scene.textures.get(key);
  if (texture && 'refresh' in texture && typeof texture.refresh === 'function') texture.refresh();
}

function fillRound(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
  ctx.fill();
}

function nineSliceWood(
  ctx: CanvasRenderingContext2D,
  img: CanvasImageSource & Measured,
  dw: number,
  dh: number,
): void {
  const sw = img.width;
  const sh = img.height;
  const left = Math.min(SLICE.wood.left, Math.floor(sw / 3));
  const right = Math.min(SLICE.wood.right, Math.floor(sw / 3));
  const top = Math.min(SLICE.wood.top, Math.floor(sh / 3));
  const bottom = Math.min(SLICE.wood.bottom, Math.floor(sh / 3));
  const srcMidW = sw - left - right;
  const srcMidH = sh - top - bottom;
  const capL = Math.min(left, dw / 3);
  const capR = Math.min(right, dw / 3);
  const capT = Math.min(top, dh / 3);
  const capB = Math.min(bottom, dh / 3);
  const midW = Math.max(1, dw - capL - capR);
  const midH = Math.max(1, dh - capT - capB);
  const blit = (sx: number, sy: number, w: number, h: number, dx: number, dy: number, bw: number, bh: number) => {
    if (w <= 0 || h <= 0 || bw <= 0 || bh <= 0) return;
    ctx.drawImage(img, sx, sy, w, h, dx, dy, bw, bh);
  };
  blit(0, 0, left, top, 0, 0, capL, capT);
  blit(left, 0, srcMidW, top, capL, 0, midW, capT);
  blit(sw - right, 0, right, top, capL + midW, 0, capR, capT);
  blit(0, top, left, srcMidH, 0, capT, capL, midH);
  blit(left, top, srcMidW, srcMidH, capL, capT, midW, midH);
  blit(sw - right, top, right, srcMidH, capL + midW, capT, capR, midH);
  blit(0, sh - bottom, left, bottom, 0, capT + midH, capL, capB);
  blit(left, sh - bottom, srcMidW, bottom, capL, capT + midH, midW, capB);
  blit(sw - right, sh - bottom, right, bottom, capL + midW, capT + midH, capR, capB);
}

function paintProceduralWood(ctx: CanvasRenderingContext2D, w: number, h: number): void {
  const face = ctx.createLinearGradient(0, 0, 0, h);
  face.addColorStop(0, '#e7c48a');
  face.addColorStop(0.18, '#c4844a');
  face.addColorStop(0.55, '#a56b32');
  face.addColorStop(1, '#6b3e22');
  ctx.fillStyle = face;
  fillRound(ctx, 0, 0, w, h, 36);
  ctx.fillStyle = '#3a2414';
  fillRound(ctx, 10, 12, w - 20, h - 22, 30);
  const inner = ctx.createLinearGradient(0, 16, 0, h - 16);
  inner.addColorStop(0, '#f0d2a0');
  inner.addColorStop(0.2, '#d3924e');
  inner.addColorStop(1, '#8a5430');
  ctx.fillStyle = inner;
  fillRound(ctx, 18, 16, w - 36, h - 34, 24);
  ctx.strokeStyle = 'rgba(255, 236, 196, 0.55)';
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.globalAlpha = 0.28;
  ctx.strokeStyle = '#6a3d1c';
  ctx.lineWidth = 2;
  for (let y = 40; y < h - 36; y += 11) {
    ctx.beginPath();
    ctx.moveTo(36, y);
    ctx.bezierCurveTo(w * 0.3, y - 3, w * 0.7, y + 4, w - 36, y - 1);
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

function paintSprig(ctx: CanvasRenderingContext2D, x: number, y: number, flipX: boolean, flipY: boolean): void {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(flipX ? -1 : 1, flipY ? -1 : 1);
  ctx.lineCap = 'round';
  ctx.strokeStyle = '#2f6a34';
  ctx.lineWidth = 5;
  ctx.beginPath();
  ctx.moveTo(6, 10);
  ctx.quadraticCurveTo(34, -2, 58, 22);
  ctx.quadraticCurveTo(74, 40, 46, 54);
  ctx.stroke();
  ctx.fillStyle = '#6e8f4e';
  ctx.beginPath();
  ctx.ellipse(28, 8, 16, 8, -0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#8fbf7a';
  ctx.beginPath();
  ctx.ellipse(22, 6, 7, 3.5, -0.6, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#3d6a32';
  ctx.beginPath();
  ctx.ellipse(52, 36, 14, 7, 0.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#f4efe4';
  fillRound(ctx, 58, 40, 8, 16, 3);
  ctx.fillStyle = '#c4473a';
  ctx.beginPath();
  ctx.ellipse(62, 38, 14, 10, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#f2e2b8';
  ctx.beginPath();
  ctx.arc(56, 36, 2.2, 0, Math.PI * 2);
  ctx.arc(66, 34, 1.8, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function paintSign(scene: Phaser.Scene, width: number, height: number): string {
  const key = `glen-load-sign-${width}x${height}`;
  if (scene.textures.exists(key)) return key;
  const ctx = makeCanvas(scene, key, width, height);
  if (!ctx) return key;
  const wood = loaderWoodImage() ?? realSource(scene, TEX.panelWood);
  if (wood) nineSliceWood(ctx, wood, width, height);
  else paintProceduralWood(ctx, width, height);
  paintSprig(ctx, 18, 16, false, false);
  paintSprig(ctx, width - 18, 16, true, false);
  paintSprig(ctx, 8, height - 18, false, true);
  paintSprig(ctx, width - 8, height - 18, true, true);
  ctx.fillStyle = '#2a160c';
  ctx.beginPath();
  ctx.arc(width * 0.22, 28, 7, 0, Math.PI * 2);
  ctx.arc(width * 0.78, 28, 7, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#e7c98a';
  ctx.beginPath();
  ctx.arc(width * 0.22, 26, 3, 0, Math.PI * 2);
  ctx.arc(width * 0.78, 26, 3, 0, Math.PI * 2);
  ctx.fill();
  publish(scene, key);
  return key;
}

function featherVertical(ctx: CanvasRenderingContext2D, w: number, h: number, top: number, bottom: number): void {
  ctx.globalCompositeOperation = 'destination-in';
  const fade = ctx.createLinearGradient(0, 0, 0, h);
  fade.addColorStop(0, `rgba(0,0,0,${top})`);
  fade.addColorStop(0.22, 'rgba(0,0,0,1)');
  fade.addColorStop(0.78, 'rgba(0,0,0,1)');
  fade.addColorStop(1, `rgba(0,0,0,${bottom})`);
  ctx.fillStyle = fade;
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'source-over';
}

function paintPhotoLayers(scene: Phaser.Scene, photo: CanvasImageSource & Measured): void {
  const farW = 420;
  const farH = 746;
  const far = makeCanvas(scene, LOADER_TEX.far, farW, farH);
  if (far) {
    const tiny = document.createElement('canvas');
    tiny.width = 240;
    tiny.height = 427;
    const tinyCtx = tiny.getContext('2d');
    if (tinyCtx) {
      tinyCtx.drawImage(photo, 0, 0, tiny.width, tiny.height);
      far.drawImage(tiny, 0, 0, farW, farH);
    } else {
      far.drawImage(photo, 0, 0, farW, farH);
    }
    const wash = far.createLinearGradient(0, 0, 0, farH);
    wash.addColorStop(0, 'rgba(255, 214, 150, 0.28)');
    wash.addColorStop(0.45, 'rgba(255, 214, 150, 0)');
    wash.addColorStop(1, 'rgba(42, 22, 12, 0.18)');
    far.fillStyle = wash;
    far.fillRect(0, 0, farW, farH);
    publish(scene, LOADER_TEX.far);
  }

  const midW = 440;
  const midH = 360;
  const mid = makeCanvas(scene, LOADER_TEX.mid, midW, midH);
  if (mid) {
    const cropY = Math.round(photo.height * 0.34);
    const cropH = Math.round(photo.height * 0.4);
    mid.drawImage(photo, 0, cropY, photo.width, cropH, 0, 0, midW, midH);
    featherVertical(mid, midW, midH, 0, 0);
    publish(scene, LOADER_TEX.mid);
  }

  const nearW = 460;
  const nearH = 280;
  const near = makeCanvas(scene, LOADER_TEX.near, nearW, nearH);
  if (near) {
    const cropY = Math.round(photo.height * 0.62);
    const cropH = photo.height - cropY;
    near.drawImage(photo, 0, cropY, photo.width, cropH, 0, 0, nearW, nearH);
    featherVertical(near, nearW, nearH, 0, 1);
    publish(scene, LOADER_TEX.near);
  }
}

function blob(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  rx: number,
  ry: number,
  color: string,
): void {
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.ellipse(x, y, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
}

function paintProceduralLayers(scene: Phaser.Scene): void {
  const farW = 420;
  const farH = 746;
  const far = makeCanvas(scene, LOADER_TEX.far, farW, farH);
  if (far) {
    const sky = far.createLinearGradient(0, 0, 0, farH);
    sky.addColorStop(0, '#f6d7a8');
    sky.addColorStop(0.42, '#e7b07a');
    sky.addColorStop(0.72, '#c98a62');
    sky.addColorStop(1, '#6e8f62');
    far.fillStyle = sky;
    far.fillRect(0, 0, farW, farH);
    const sun = far.createRadialGradient(farW * 0.72, farH * 0.22, 10, farW * 0.72, farH * 0.22, 180);
    sun.addColorStop(0, 'rgba(255, 236, 190, 0.95)');
    sun.addColorStop(1, 'rgba(255, 236, 190, 0)');
    far.fillStyle = sun;
    far.fillRect(0, 0, farW, farH);
    const canopies: Array<[number, number, number, number, string]> = [
      [70, 250, 90, 70, 'rgba(126, 160, 106, 0.55)'],
      [150, 220, 110, 80, 'rgba(94, 125, 82, 0.5)'],
      [250, 260, 120, 86, 'rgba(143, 175, 120, 0.45)'],
      [340, 230, 100, 74, 'rgba(90, 122, 78, 0.5)'],
      [40, 340, 80, 60, 'rgba(70, 100, 64, 0.4)'],
      [380, 330, 90, 64, 'rgba(78, 110, 70, 0.42)'],
    ];
    for (const [x, y, rx, ry, color] of canopies) blob(far, x, y, rx, ry, color);
    publish(scene, LOADER_TEX.far);
  }

  const mid = makeCanvas(scene, LOADER_TEX.mid, 440, 360);
  if (mid) {
    mid.clearRect(0, 0, 440, 360);
    const trees: Array<[number, number, number, string]> = [
      [70, 150, 78, '#5e7d52'],
      [150, 120, 96, '#3f6a3a'],
      [300, 140, 110, '#6e8f4e'],
      [390, 170, 80, '#4e7344'],
    ];
    for (const [x, y, r, color] of trees) {
      mid.fillStyle = '#5c3a24';
      fillRound(mid, x - 10, y + 20, 20, 150, 8);
      blob(mid, x, y, r, r * 0.72, color);
      blob(mid, x - r * 0.2, y - r * 0.15, r * 0.45, r * 0.28, 'rgba(255, 236, 196, 0.18)');
    }
    featherVertical(mid, 440, 360, 0, 0);
    publish(scene, LOADER_TEX.mid);
  }

  const near = makeCanvas(scene, LOADER_TEX.near, 460, 280);
  if (near) {
    const bank = near.createLinearGradient(0, 0, 0, 280);
    bank.addColorStop(0, 'rgba(110, 143, 78, 0)');
    bank.addColorStop(0.35, '#7ea06a');
    bank.addColorStop(1, '#3d5a34');
    near.fillStyle = bank;
    near.fillRect(0, 0, 460, 280);
    const pond = near.createRadialGradient(230, 130, 20, 230, 140, 150);
    pond.addColorStop(0, '#d5ece4');
    pond.addColorStop(0.45, '#7eb8b0');
    pond.addColorStop(1, '#3e6e68');
    near.fillStyle = pond;
    near.beginPath();
    near.ellipse(230, 145, 150, 52, 0, 0, Math.PI * 2);
    near.fill();
    near.strokeStyle = 'rgba(255, 244, 220, 0.45)';
    near.lineWidth = 2;
    near.beginPath();
    near.ellipse(220, 138, 90, 18, 0, 0, Math.PI * 2);
    near.stroke();
    featherVertical(near, 460, 280, 0, 1);
    publish(scene, LOADER_TEX.near);
  }
}

function paintLight(scene: Phaser.Scene): void {
  if (scene.textures.exists(LOADER_TEX.light)) return;
  const ctx = makeCanvas(scene, LOADER_TEX.light, 256, 256);
  if (!ctx) return;
  const glow = ctx.createRadialGradient(128, 128, 8, 128, 128, 128);
  glow.addColorStop(0, 'rgba(255, 228, 170, 0.85)');
  glow.addColorStop(0.45, 'rgba(255, 196, 120, 0.28)');
  glow.addColorStop(1, 'rgba(255, 196, 120, 0)');
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, 256, 256);
  publish(scene, LOADER_TEX.light);
}

function paintMotes(scene: Phaser.Scene): void {
  if (!scene.textures.exists(LOADER_TEX.fly)) {
    const ctx = makeCanvas(scene, LOADER_TEX.fly, 32, 32);
    if (ctx) {
      const glow = ctx.createRadialGradient(16, 16, 1, 16, 16, 16);
      glow.addColorStop(0, 'rgba(255, 248, 210, 1)');
      glow.addColorStop(0.35, 'rgba(232, 208, 90, 0.85)');
      glow.addColorStop(1, 'rgba(232, 208, 90, 0)');
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, 32, 32);
      publish(scene, LOADER_TEX.fly);
    }
  }
  if (!scene.textures.exists(LOADER_TEX.leaf)) {
    const ctx = makeCanvas(scene, LOADER_TEX.leaf, 28, 16);
    if (ctx) {
      ctx.fillStyle = '#c47848';
      ctx.beginPath();
      ctx.ellipse(14, 8, 12, 6, -0.4, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = '#6b3e22';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(4, 10);
      ctx.quadraticCurveTo(14, 6, 24, 7);
      ctx.stroke();
      publish(scene, LOADER_TEX.leaf);
    }
  }
  if (!scene.textures.exists(LOADER_TEX.seed)) {
    const ctx = makeCanvas(scene, LOADER_TEX.seed, 20, 36);
    if (ctx) {
      ctx.strokeStyle = 'rgba(90, 70, 40, 0.7)';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(10, 14);
      ctx.lineTo(10, 34);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 252, 245, 0.95)';
      for (const [x, y, r] of [
        [10, 8, 5],
        [6, 11, 3.5],
        [14, 11, 3.5],
        [10, 13, 3],
      ] as const) {
        ctx.beginPath();
        ctx.arc(x, y, r, 0, Math.PI * 2);
        ctx.fill();
      }
      publish(scene, LOADER_TEX.seed);
    }
  }
}

function paintHoney(scene: Phaser.Scene): void {
  if (scene.textures.exists(LOADER_TEX.honey)) return;
  const w = 256;
  const h = 36;
  const ctx = makeCanvas(scene, LOADER_TEX.honey, w, h);
  if (!ctx) return;
  const honey = ctx.createLinearGradient(0, 0, 0, h);
  honey.addColorStop(0, '#fff1b8');
  honey.addColorStop(0.45, '#f0c14a');
  honey.addColorStop(1, '#d4842a');
  ctx.fillStyle = honey;
  ctx.fillRect(0, 0, w, h);
  ctx.strokeStyle = '#3f6b32';
  ctx.lineWidth = 3;
  ctx.lineCap = 'round';
  ctx.beginPath();
  for (let x = 0; x <= w; x += 8) {
    const y = h * 0.55 + Math.sin(x * 0.08) * 6;
    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.strokeStyle = '#8fbf7a';
  ctx.lineWidth = 2;
  ctx.globalAlpha = 0.85;
  ctx.beginPath();
  for (let x = 0; x <= w; x += 8) {
    const y = h * 0.38 + Math.sin(x * 0.08 + 1.2) * 5;
    if (x === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.stroke();
  ctx.globalAlpha = 1;
  ctx.fillStyle = 'rgba(255, 255, 240, 0.45)';
  ctx.fillRect(0, 4, w, 6);
  publish(scene, LOADER_TEX.honey);
}

function paintShimmer(scene: Phaser.Scene): void {
  if (scene.textures.exists(LOADER_TEX.shimmer)) return;
  const ctx = makeCanvas(scene, LOADER_TEX.shimmer, 48, 32);
  if (!ctx) return;
  const band = ctx.createLinearGradient(0, 0, 48, 0);
  band.addColorStop(0, 'rgba(255,255,255,0)');
  band.addColorStop(0.5, 'rgba(255,255,255,0.85)');
  band.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = band;
  ctx.fillRect(0, 0, 48, 32);
  publish(scene, LOADER_TEX.shimmer);
}

function paintAcorn(scene: Phaser.Scene): void {
  if (scene.textures.exists(LOADER_TEX.acorn)) return;
  const ctx = makeCanvas(scene, LOADER_TEX.acorn, 40, 40);
  if (!ctx) return;
  ctx.fillStyle = '#8a5430';
  ctx.beginPath();
  ctx.ellipse(20, 24, 11, 12, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#c4844a';
  ctx.beginPath();
  ctx.ellipse(17, 22, 4, 6, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#5c3418';
  ctx.beginPath();
  ctx.ellipse(20, 14, 12, 7, 0, Math.PI, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#3a2414';
  ctx.lineWidth = 1;
  for (let i = -2; i <= 2; i += 1) {
    ctx.beginPath();
    ctx.moveTo(20 + i * 4, 10);
    ctx.lineTo(20 + i * 4, 16);
    ctx.stroke();
  }
  publish(scene, LOADER_TEX.acorn);
}

/** Bake the glen plates, the sign, and the mote sprites. Textures are tiny and painted once. */
export function ensureLoaderPaint(scene: Phaser.Scene, signW: number, signH: number): string {
  const photo = loaderGlenImage() ?? realSource(scene, TEX.bgGame);
  if (photo && !scene.textures.exists(LOADER_TEX.far)) paintPhotoLayers(scene, photo);
  if (!scene.textures.exists(LOADER_TEX.far)) paintProceduralLayers(scene);
  paintLight(scene);
  paintMotes(scene);
  paintHoney(scene);
  paintShimmer(scene);
  paintAcorn(scene);
  return paintSign(scene, signW, signH);
}
